import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { 
    GET_COMMISSION_STATEMENTS, 
    UPDATE_STATEMENT_STATUS,
    MARK_STATEMENT_AS_PAID 
} from '../../graphql/sales-commission-statements';

const CommissionStatements = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statements, setStatements] = useState({});
    const [sortBy, setSortBy] = useState('generatedDate');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [paymentFilter, setPaymentFilter] = useState('ALL');
    
    const query = useQuery(GET_COMMISSION_STATEMENTS, {
        variables: {
            pageIndex: 0,
            pageSize: 20,
            orderAsc: false,
            orderField: 'generatedDate',
            filters: {}
        },
        onCompleted: (data) => {
            setStatements(data?.salesCommissionStatements);
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            const filters = {};
            
            // Apply status filter
            if (statusFilter !== 'ALL') {
                filters.status = statusFilter;
            }
            
            // Apply payment filter - will be handled client-side since no direct paymentStatus in schema
            
            // Apply search filter
            if (searchTerm.trim()) {
                filters.search = searchTerm;
            }

            const { data } = await query.refetch({
                pageIndex: 0,
                pageSize: 20,
                orderAsc: false,
                orderField: sortBy,
                filters: filters
            });
            setStatements(data?.salesCommissionStatements);
        };

        const debounceTimer = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, sortBy, statusFilter, paymentFilter, query]);

    const [updateStatus] = useMutation(UPDATE_STATEMENT_STATUS, {
        onCompleted: () => {
            query.refetch();
        },
        onError: (error) => {
            console.error('Error updating status:', error);
            alert('Failed to update statement status. Please try again.');
        }
    });

    const [recordPayment] = useMutation(MARK_STATEMENT_AS_PAID, {
        onCompleted: () => {
            query.refetch();
        },
        onError: (error) => {
            console.error('Error recording payment:', error);
            alert('Failed to record payment. Please try again.');
        }
    });

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            DRAFT: 'bg-gray-100 text-gray-800',
            GENERATED: 'bg-blue-100 text-blue-800',
            SENT: 'bg-yellow-100 text-yellow-800',
            PAID: 'bg-green-100 text-green-800',
            DISPUTED: 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getPaymentStatus = (statement) => {
        if (statement.status === 'PAID' || statement.paidDate) {
            return 'PAID';
        }
        if (statement.paymentInfo?.paymentReceived && statement.paymentInfo?.paymentReceived > 0) {
            return statement.paymentInfo.paymentReceived >= statement.finalCommissionAmount ? 'PAID' : 'PARTIAL';
        }
        if (statement.paymentInfo?.paymentDue && new Date(statement.paymentInfo.paymentDue) < new Date()) {
            return 'OVERDUE';
        }
        return 'PENDING';
    };

    const getPaymentStatusColor = (status) => {
        const colors = {
            PENDING: 'bg-yellow-100 text-yellow-800',
            PARTIAL: 'bg-blue-100 text-blue-800',
            PAID: 'bg-green-100 text-green-800',
            OVERDUE: 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const handleStatusChange = (id, newStatus) => {
        if (window.confirm(`Are you sure you want to change the status to ${newStatus}?`)) {
            updateStatus({ 
                variables: { 
                    id, 
                    status: newStatus,
                    statusData: `Status changed to ${newStatus} on ${new Date().toLocaleDateString()}`
                } 
            });
        }
    };

    if (query.loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (query.error) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {query.error.message}</span>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m2-10v18a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2h8a2 2 0 012 2z" />
                        </svg>
                        Commission Statements
                    </h1>
                    <p className="text-gray-600">Manage agent commission statements and payments</p>
                </div>
                <Link
                    to="/sales/commission-statements/generate"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
                >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                    </svg>
                    <span>Generate Statement</span>
                </Link>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Search */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            placeholder="Statement number, agent name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                    </div>

                    {/* Status Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Statement Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="ALL">All Statuses</option>
                            <option value="DRAFT">Draft</option>
                            <option value="GENERATED">Generated</option>
                            <option value="SENT">Sent</option>
                            <option value="PAID">Paid</option>
                            <option value="DISPUTED">Disputed</option>
                        </select>
                    </div>

                    {/* Payment Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                        <select
                            value={paymentFilter}
                            onChange={(e) => setPaymentFilter(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="ALL">All Payments</option>
                            <option value="PENDING">Pending</option>
                            <option value="PARTIAL">Partial</option>
                            <option value="PAID">Paid</option>
                            <option value="OVERDUE">Overdue</option>
                        </select>
                    </div>

                    {/* Sort */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                            <option value="generatedDate">Generated Date</option>
                            <option value="statementNumber">Statement Number</option>
                            <option value="finalCommissionAmount">Commission Amount</option>
                            <option value="agentId.lastName">Agent Name</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Commission Statements List */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Statement
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Agent
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Commission
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {statements?.items?.map((statement) => (
                                <tr key={statement.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {statement.statementNumber}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {formatDate(statement.generatedDate || statement.createdAt)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {statement.agentId.firstName} {statement.agentId.lastName}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {statement.totalDeals} deals
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {formatCurrency(statement.finalCommissionAmount)}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            VAT: {formatCurrency(statement.totalVATAmount)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(statement.status)}`}>
                                            {statement.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(getPaymentStatus(statement))}`}>
                                            {getPaymentStatus(statement)}
                                        </span>
                                        {statement.paidDate && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                Paid: {formatDate(statement.paidDate)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                        <Link
                                            to={`/sales/commission-statements/${statement.id}`}
                                            className="text-blue-600 hover:text-blue-900"
                                        >
                                            View
                                        </Link>
                                        {statement.status !== 'PAID' && (
                                            <button
                                                onClick={() => handleStatusChange(statement.id, 'SENT')}
                                                className="text-green-600 hover:text-green-900"
                                            >
                                                Send
                                            </button>
                                        )}
                                        {getPaymentStatus(statement) !== 'PAID' && (
                                            <button
                                                onClick={() => {
                                                    const amount = prompt('Enter payment amount:');
                                                    if (amount && !isNaN(parseFloat(amount))) {
                                                        recordPayment({
                                                            variables: {
                                                                id: statement.id,
                                                                inputData: {
                                                                    paymentReceived: parseFloat(amount),
                                                                    paymentDate: new Date().toISOString(),
                                                                    paymentMethod: 'Bank Transfer',
                                                                    paymentReference: `Payment for ${statement.statementNumber}`
                                                                }
                                                            }
                                                        });
                                                    }
                                                }}
                                                className="text-purple-600 hover:text-purple-900"
                                            >
                                                Record Payment
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Empty State */}
            {statements?.items?.length === 0 && (
                <div className="text-center py-12">
                    <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m2-10v18a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2h8a2 2 0 012 2z"/>
                    </svg>
                    <p className="text-gray-500 text-lg">No commission statements found</p>
                    <p className="text-gray-400 text-sm">Generate your first commission statement to get started</p>
                    <Link
                        to="/sales/commission-statements/generate"
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                        Generate Statement
                    </Link>
                </div>
            )}
        </div>
    );
};

export default CommissionStatements;